import React, { useCallback, useEffect, useRef } from 'react';
import { isTouchLike } from '../../../ui/pointerType';
import './BoardDock.css';

/** Ein Tipp auf etwas Bedienbares im Dock (Handkarte, Kachel, Knopf) gehört diesem, nicht dem Dock. */
const INTERACTIVE = 'button, [role="button"], a, input, .zone-tile';

/** So weit darf der Zeiger das gehobene Dock verlassen, bevor es sinkt: die Kante flattert nicht. */
export const DOCK_HOVER_TOLERANCE_PX = 8;


/**
 * Unteres Dock des Bretts (Spec 2026-10-21 §5.2, §14): Zonen-Kacheln links und rechts, der
 * Handfächer dazwischen — eine Einheit, die in Ruhe um die Hälfte ihrer Höhe unter den Rand
 * rutscht und gehoben ganz zu sehen ist.
 *
 *   <BoardDock ref={measureRef} raised={raised} onRaisedChange={setRaised} isMobile={isMobile}
 *     handLowered={isMobile && dragging}
 *     tiles={{ left: <>{graveyardTile}{exileTile}</>, right: <>{commandTile}{deckTile}</> }}
 *     hand={<HandFan … />} />
 *
 * - Lage: absolut an der Unterkante des Containers, über der ganzen Breite; der Container kappt,
 *   was unter den Rand rutscht. Kacheln hängen an den Außenkanten (`tiles.left` von links,
 *   `tiles.right` von rechts, in DOM-Reihenfolge); jede Seite hat Platz für zwei Kacheln.
 * - Die Hand-Reihe steht mittig mit fester Breite (Fenster minus zwei Kachelbreiten je Seite,
 *   `board-dock-hand`) und außerhalb des Flusses. Eine Kachel, die kommt oder geht (Exil beim
 *   Zug), verschiebt deshalb weder Hand noch Nachbarkacheln: dnd-kit mäße die gezogene Karte
 *   nach einer Verschiebung neu, und Overlay und Ablage lägen neben dem Zeiger.
 * - `raised` gehört der Seite; am Handy hält sie das Dock während eines Zugs gehoben, damit jede
 *   Kachel ganz als Ablageziel bereitsteht. Am Rechner hebt auch während eines Zugs nur der
 *   Zeiger im Dock-Rechteck (der `pointermove` am `document` läuft beim Ziehen weiter).
 * - Rechner: Hover entscheidet die Lage, nicht das Element unter dem Zeiger. Ein `pointermove` der
 *   Maus am `document` vergleicht den Zeiger mit dem Rechteck des Docks (samt Lücken und
 *   Hand-Reihe; in Ruhe zählt nur der sichtbare Teil, weil das Rechteck die Verschiebung enthält):
 *   darin → `onRaisedChange(true)`, außerhalb → `false`, gemeldet nur bei einem Wechsel. Gehoben
 *   sinkt es erst `DOCK_HOVER_TOLERANCE_PX` außerhalb, und sobald der Zeiger das Fenster verlässt
 *   (danach käme kein `pointermove` mehr, das außerhalb läge). Das Dock selbst fängt keinen
 *   Zeiger (nur Kacheln und Kartenreihe), damit das Brett im Dock-Streifen klickbar, ziehbar und
 *   zoombar bleibt; eine Hover-Fläche aus einem Element nähme ihm genau das. Enter/Leave an
 *   Elementen ließe das Dock dagegen in jeder Lücke sinken und wieder steigen.
 * - Tipp-Weg (Handy, `isMobile`, und am Rechner jeder Finger oder Stift, `pointerType` ≠ mouse —
 *   etwa ein Tablet über 900 px): ein Tipp auf freien Dock-Bereich (zwischen den Handkarten)
 *   schaltet um, ein Tipp außerhalb des gehobenen Docks senkt es; Tipps auf Handkarten,
 *   Kacheln und Knöpfe lösen nur diese aus. Ist im Fächer eine Karte herausgehoben
 *   (`[data-lifted]`), senkt der Tipp außerhalb nur sie, erst der nächste das Dock.
 * - `handLowered`: die Hand-Reihe allein rutscht um die halbe Dock-Höhe nach unten (Handy beim
 *   Ziehen: der Fächer gibt das Brett frei, die Kacheln bleiben ganz erreichbar). Sie steht dann
 *   etwa dort, wo sie im gesenkten Dock stünde, und ist nur dort Ablageziel.
 * - Kartengröße: `--dock-card-w` (Rechner 150 px, Handy so groß, wie die Fensterhöhe quer
 *   zulässt: 26 % davon, 72–110 px) gilt für Handkarten und Kacheln gleich. Die Höhe des Docks
 *   misst die Seite über `ref` (Toasts, Aktionsleiste).
 * - Ablageziele in Kacheln und Fächer werden samt Transform gemessen (`DROP_MEASURING`), zählen
 *   also dort, wo das Dock gerade steht.
 * - Prüfanker: `data-testid="board-dock"` mit `data-raised` und `data-hand-lowered`,
 *   `data-testid="board-dock-hand"` (Hand-Reihe).
 */
const BoardDock = React.forwardRef(function BoardDock({ raised, onRaisedChange, isMobile, handLowered = false, tiles, hand }, ref) {
  const rootRef = useRef(null);
  const setRoot = useCallback((node) => {
    rootRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  }, [ref]);

  // Zuletzt gemeldeter bzw. von der Seite gesetzter Stand: gemeldet wird nur ein Wechsel.
  const shownRef = useRef(raised);
  useEffect(() => {
    shownRef.current = raised;
  }, [raised]);
  const onRaisedChangeRef = useRef(onRaisedChange);
  onRaisedChangeRef.current = onRaisedChange;

  // Rechner: Hover über die Lage des Zeigers zum Dock-Rechteck.
  useEffect(() => {
    if (isMobile) return undefined;
    const handleMove = (event) => {
      if (isTouchLike(event)) return;
      const root = rootRef.current;
      if (!root) return;
      const rect = root.getBoundingClientRect();
      const margin = shownRef.current ? DOCK_HOVER_TOLERANCE_PX : 0;
      const inside = event.clientX >= rect.left - margin && event.clientX <= rect.right + margin
        && event.clientY >= rect.top - margin && event.clientY <= rect.bottom + margin;
      if (inside === shownRef.current) return;
      shownRef.current = inside;
      onRaisedChangeRef.current?.(inside);
    };
    const handleLeaveWindow = (event) => {
      if (event.relatedTarget !== null || !shownRef.current) return;
      shownRef.current = false;
      onRaisedChangeRef.current?.(false);
    };
    document.addEventListener('pointermove', handleMove, true);
    document.addEventListener('mouseout', handleLeaveWindow);
    return () => {
      document.removeEventListener('pointermove', handleMove, true);
      document.removeEventListener('mouseout', handleLeaveWindow);
    };
  }, [isMobile]);

  // Tipp-Weg: ein Finger außerhalb des gehobenen Docks senkt es.
  useEffect(() => {
    if (!raised) return undefined;
    const handleOutside = (event) => {
      if (!isMobile && !isTouchLike(event)) return;
      const root = rootRef.current;
      if (!root || root.contains(event.target)) return;
      if (root.querySelector('[data-lifted]')) return;
      onRaisedChange?.(false);
    };
    document.addEventListener('pointerdown', handleOutside, true);
    return () => document.removeEventListener('pointerdown', handleOutside, true);
  }, [isMobile, raised, onRaisedChange]);

  // Ob der letzte Druck im Dock ein Finger oder Stift war: dann gilt auch am Rechner der Tipp-Weg.
  const touchPressRef = useRef(false);
  const handlers = {
    onPointerDown: (event) => {
      touchPressRef.current = isTouchLike(event);
    },
    onClick: (event) => {
      if (!isMobile && !touchPressRef.current) return;
      if (event.target.closest?.(INTERACTIVE)) return;
      onRaisedChange?.(!raised);
    },
  };

  return (
    <div
      ref={setRoot}
      className={`board-dock${isMobile ? ' board-dock--mobile' : ''}`}
      data-testid="board-dock"
      data-raised={raised ? 'true' : 'false'}
      data-hand-lowered={handLowered ? 'true' : 'false'}
      {...handlers}
    >
      <div className="board-dock__side board-dock__side--left">{tiles?.left}</div>
      <div className="board-dock__hand" data-testid="board-dock-hand">
        {hand}
      </div>
      <div className="board-dock__side board-dock__side--right">{tiles?.right}</div>
    </div>
  );
});

export default BoardDock;
